import { useBreakpointValue } from '@chakra-ui/react';
import {
  addDays,
  eachDayOfInterval,
  format,
  getDay,
  intervalToDuration,
  isSameDay,
  startOfWeek,
  subDays,
} from 'date-fns';
import { fr } from 'date-fns/locale';
import { MindbodyClass } from 'types/class';
import { ClientVisit } from 'types/client';
import { ExpirationUnitTranslations } from 'types/services';

const numberOfDays = mondayMindbody?.customization?.numberOfDays ?? 8;

/**
 * Generates an array of available dates for booking.
 *
 * Either from current monday +8 days if monday 12:00 has already passed or from last week's monday +8 days. (8 or numberOfDays)
 * @returns number[]
 */
export function getBookableDays() {
  const now = new Date();
  const day = getDay(now);
  const hour = now.getHours();

  const isMonday = day === 1;
  const isPastNoon = hour >= 12;
  if (isMonday && !isPastNoon) {
    // Get last week's monday by substracting 7 days to current date
    const lastMonday = startOfWeek(subDays(now, 7), { weekStartsOn: 1 });

    return eachDayOfInterval({
      start: lastMonday,
      end: addDays(lastMonday, numberOfDays),
    });
  }

  const monday = startOfWeek(now, { weekStartsOn: 1 });
  return eachDayOfInterval({
    start: monday,
    end: addDays(monday, numberOfDays),
  });
}

export function isClassBookable(mindbodyClass: MindbodyClass) {
  return (
    new Date(mindbodyClass.StartDateTime) > new Date() &&
    getBookableDays().some((bookableDay) => isSameDay(bookableDay, new Date(mindbodyClass.StartDateTime)))
  );
}

export function getClassRemainingCapacity(mindbodyClass: MindbodyClass) {
  return mindbodyClass.MaxCapacity - mindbodyClass.TotalBooked;
}

export function classHasCapacity(mindbodyClass: MindbodyClass) {
  return getClassRemainingCapacity(mindbodyClass) > 0;
}

export function isVisitLateCancel(visit: ClientVisit) {
  const start = new Date();
  const end = new Date(visit.StartDateTime);
  const interval = intervalToDuration({ start, end });
  return (interval?.days ?? 0) < 1;
}

export const formatExpiration = (length: number, unit: string | undefined) => {
  if (!length || !unit) return '';

  const unitTranslations: ExpirationUnitTranslations = {
    day: { singular: 'jour', plural: 'jours' },
    days: { singular: 'jour', plural: 'jours' },
    month: { singular: 'mois', plural: 'mois' },
    months: { singular: 'mois', plural: 'mois' },
  };

  const { singular, plural } = unitTranslations[unit.toLowerCase()] || { singular: '', plural: '' };
  const unitText = length > 1 ? plural : singular;

  return `Valable ${length} ${unitText}`;
};

export function isValidEmail(email: string) {
  // eslint-disable-next-line no-useless-escape
  return /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(
    email,
  );
}

export const getDayAbbreviation = (date: Date) => {
  const day = format(date, 'EEEE', { locale: fr });
  switch (day) {
    case 'lundi':
      return 'L';
    case 'mardi':
      return 'Ma';
    case 'mercredi':
      return 'Me';
    case 'jeudi':
      return 'J';
    case 'vendredi':
      return 'V';
    case 'samedi':
      return 'S';
    case 'dimanche':
      return 'D';
    default:
      return '';
  }
};

export const toSlug = (text: string) => {
  return text
    .toString() // Convertit en chaîne, au cas où
    .normalize('NFD') // Décompose les caractères accentués en composants (NFD mode)
    .replace(/[\u0300-\u036f]/g, '') // Supprime les accents
    .toLowerCase() // Convertit en minuscules
    .trim() // Supprime les espaces au début et à la fin
    .replace(/[^a-z0-9 -]/g, '') // Supprime les caractères non alphanumériques
    .replace(/\s+/g, '-') // Remplace les espaces par des traits d'union
    .replace(/-+/g, '-'); // Remplace les séquences multiples de trait d'union par un seul trait d'union
};

export const capitalizeFirstLetter = (text: string) => {
  return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
};

export const stripHtml = (html: string) => {
  const temporalDivElement = document.createElement('div');
  temporalDivElement.innerHTML = html;
  return temporalDivElement.textContent || temporalDivElement.innerText || '';
};

export function useIsMobile() {
  const isMobile = useBreakpointValue({ base: true, md: false });
  return isMobile;
}
